import { useId } from "react";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { MenuEntry, MenuEntryId } from "./drawerLogic";

interface QuoteActionsMenuProps {
  entries: (MenuEntry | "sep")[];
  onSelect: (id: MenuEntryId) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Entries whose action is running, shown disabled. */
  busy?: Partial<Record<MenuEntryId, boolean>>;
}

/**
 * The ⋯ menu (#1559), built from menuEntries(). Each entry is named by its
 * label alone and described by its grey sub-label, so a disabled entry's
 * reason is read out without changing its name.
 */
export function QuoteActionsMenu({ entries, onSelect, open, onOpenChange, busy = {} }: QuoteActionsMenuProps) {
  const base = useId();
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="More actions"
          className="grid h-[34px] w-[34px] flex-none place-items-center rounded-[7px] border bg-card text-muted-foreground hover:bg-muted"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[230px] p-1.5">
        {entries.map((e, i) => {
          if (e === "sep") return <DropdownMenuSeparator key={`sep-${i}`} />;
          const labelId = `${base}-${e.id}-label`;
          const subId = `${base}-${e.id}-sub`;
          return (
            <DropdownMenuItem
              key={e.id}
              disabled={e.disabled || busy[e.id]}
              aria-labelledby={labelId}
              aria-describedby={e.sub ? subId : undefined}
              onSelect={() => onSelect(e.id)}
              className={cn("grid gap-0 px-2.5 py-1.5 text-[13.5px]", e.danger && "text-status-hot")}
            >
              <span id={labelId}>{e.label}</span>
              {e.sub && (
                <small id={subId} className="text-[12px] text-muted-foreground">
                  {e.sub}
                </small>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
